"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import { useAppContext } from "../../context/AppContext";
import { Icon } from "../../icons";
import type { HeaderAction, PageMeta } from "../../types";
import { getPageMeta } from "../../utils/getPageMeta";

import { UserDropdown } from "./UserDropdown";

type TopHeaderProps = {
  locale: string;
  headerActions: HeaderAction[];
  routeMap: Record<string, PageMeta>;
  onBurgerClick: () => void;
  headerExtra?: ReactNode;
  onManageSubscription?: () => void;
};

export function TopHeader({
  locale,
  headerActions,
  routeMap,
  onBurgerClick,
  headerExtra,
  onManageSubscription,
}: TopHeaderProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const pageMeta = getPageMeta(pathname, locale, routeMap);
  const { hasUnreadMessages } = useAppContext();

  return (
    <header
      data-testid="header"
      className="flex h-20 shrink-0 items-center justify-between bg-header-bg px-5 shadow-shell-header lg:px-8"
    >
      {/* Left: Burger (mobile) / Page title (desktop) */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBurgerClick}
          data-testid="header-burger"
          className="flex size-12 items-center justify-center rounded-xl text-header-icon-hover transition-all duration-shell ease-shell hover:bg-surface-hover lg:hidden"
        >
          <Icon name="menu" size={24} />
        </button>
        <div className="hidden lg:block">
          <h1
            data-testid="header-title"
            className="text-xl font-semibold text-header-title"
          >
            {t(pageMeta.titleKey)}
          </h1>
          <p className="text-base text-header-subtitle">
            {t(pageMeta.subtitleKey)}
          </p>
        </div>
      </div>

      {/* Right: Action icons + user */}
      <div className="flex items-center gap-2">
        {headerActions.map((action) => (
          <Link
            key={action.href}
            data-testid="header-action"
            href={`/${locale}${action.href}`}
            className="relative flex size-12 items-center justify-center rounded-xl text-header-icon transition-all duration-shell ease-shell hover:bg-surface-hover hover:text-header-icon-hover"
            title={t(action.labelKey)}
          >
            <Icon name={action.icon} size={24} />
            {/* Matched on href rather than a flag on the action: every app
                configures its own header actions, and none of them should have
                to remember to opt this one in. */}
            {action.href === "/messages" && hasUnreadMessages && (
              <span
                data-testid="header-unread-dot"
                className="absolute right-2.5 top-2.5 size-2.5 rounded-full bg-red-500 ring-2 ring-header-bg"
              />
            )}
          </Link>
        ))}
        {headerExtra}
        <div className="ml-3 border-l border-surface-border pl-3">
          <UserDropdown
            locale={locale}
            onManageSubscription={onManageSubscription}
          />
        </div>
      </div>
    </header>
  );
}
